import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ProductCard from "@/components/feature/ProductCard";
import Reveal from "@/components/base/Reveal";
import { useCatalogProducts, useCategories } from "@/hooks/useProducts";
import { collections, getCollectionBySlug, getNextCollection } from "@/data/collections";
import NotFound from "@/pages/NotFound";

export default function CollectionPage() {
  const { slug } = useParams<{ slug: string }>();
  const content = slug ? getCollectionBySlug(slug) : undefined;

  // Hero load animation
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setLoaded(false);
    const t = setTimeout(() => setLoaded(true), 50);
    return () => clearTimeout(t);
  }, [slug]);

  // Resolve category id from name so the product query is accurate
  const { data: categories } = useCategories();
  const categoryId = useMemo(() => {
    if (!content) return null;
    return categories.find((c) => c.name === content.categoryName)?.id ?? null;
  }, [categories, content]);

  const { data: products, loading, error } = useCatalogProducts({
    categoryId,
    search: "",
    sort: "newest",
  });

  // 404 if slug doesn't match a known collection
  if (!content) return <NotFound />;

  return (
    <main className="min-h-screen bg-background-50 text-foreground-950">
      <Navbar transparentOverHero />

      {/* === Hero === */}
      <section className="relative h-[58vh] min-h-[420px] max-h-[600px] w-full overflow-hidden">
        <img
          src={content.heroImage}
          alt={content.heroAlt}
          title={`${content.categoryName} — Hearth & Heirloom`}
          className={`absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[2200ms] ease-out ${
            loaded ? "scale-100" : "scale-110"
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/55" />

        <div className="relative h-full w-full flex flex-col justify-end">
          <div className="px-4 md:px-10 lg:px-14 pb-12 md:pb-14 max-w-5xl">
            <p
              className={`text-background-50/80 text-xs md:text-sm tracking-[0.3em] uppercase mb-4 transition-all duration-1000 ${
                loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
              }`}
            >
              {content.eyebrow}
            </p>
            <h1
              className={`font-heading text-background-50 text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight whitespace-pre-line transition-all duration-1000 delay-150 ${
                loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              {content.headline}
            </h1>
            <p
              className={`mt-5 md:mt-6 max-w-xl text-background-50/85 text-sm md:text-base leading-relaxed transition-all duration-1000 delay-300 ${
                loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
              }`}
            >
              {content.intro}
            </p>
          </div>
        </div>

        {/* Scroll cue */}
        <div
          className={`absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-background-50/80 transition-opacity duration-1000 delay-700 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        >
          <span className="text-[10px] tracking-[0.4em] uppercase">Shop the collection</span>
          <span className="block w-px h-8 bg-background-50/60 animate-pulse" />
        </div>
      </section>

      {/* === Product grid (product-first) === */}
      <section className="px-4 md:px-10 lg:px-14 pt-14 md:pt-20 pb-20 md:pb-24">
        <Reveal from="up" className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-10 md:mb-14">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-foreground-600 mb-3">
              Shop
            </p>
            <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
              {content.productsLabel}
            </h2>
          </div>
          {!loading && !error && products.length > 0 && (
            <p className="text-sm text-foreground-600">
              {products.length} {products.length === 1 ? "piece" : "pieces"} in this collection
            </p>
          )}
        </Reveal>

        {error && (
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
            <p className="text-sm text-foreground-700 mb-3">{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-foreground-950 text-background-50 text-sm cursor-pointer whitespace-nowrap"
            >
              Try again
            </button>
          </div>
        )}

        {!error && (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
            data-product-shop
          >
            {loading &&
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-background-200/70 rounded-lg" />
                  <div className="pt-3 space-y-2">
                    <div className="h-4 bg-background-200/70 rounded w-3/4" />
                    <div className="h-3 bg-background-200/70 rounded w-1/3" />
                  </div>
                </div>
              ))}

            {!loading &&
              products.map((p, i) => (
                <Reveal key={p.id} from="up" delay={Math.min(i, 7) * 80}>
                  <ProductCard product={p} />
                </Reveal>
              ))}

            {!loading && products.length === 0 && (
              <div className="col-span-full py-16 text-center">
                <p className="text-sm text-foreground-700">
                  More pieces are joining this collection soon.
                </p>
                <Link
                  to="/"
                  className="mt-4 inline-flex items-center gap-2 px-5 h-10 rounded-full bg-foreground-950 text-background-50 text-sm cursor-pointer whitespace-nowrap"
                >
                  Browse the rest of the shop
                </Link>
              </div>
            )}
          </div>
        )}
      </section>

      {/* === Attributes strip === */}
      <section className="px-4 md:px-10 lg:px-14 py-12 md:py-16 border-y border-background-200/70 bg-background-100/40">
        <Reveal from="up" className="mb-8 md:mb-10">
          <p className="text-xs tracking-[0.3em] uppercase text-foreground-600">
            What sets this collection apart
          </p>
        </Reveal>
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-10 max-w-5xl">
          {content.attributes.map((a, i) => (
            <Reveal key={a.label} as="li" from="up" delay={i * 120}>
              <div className="group flex items-center gap-3 cursor-default">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 text-foreground-950 group-hover:bg-foreground-950 group-hover:text-background-50 transition-colors duration-500">
                  <i className={`${a.icon} text-lg`}></i>
                </span>
                <span className="text-sm md:text-base text-foreground-800">{a.label}</span>
              </div>
            </Reveal>
          ))}
        </ul>
      </section>

      {/* === Story chapter === */}
      <section className="px-4 md:px-10 lg:px-14 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-center">
          <Reveal from="left" className="lg:col-span-6 order-2 lg:order-1">
            <p className="text-xs tracking-[0.3em] uppercase text-foreground-600 mb-4">
              {content.chapter.eyebrow}
            </p>
            <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-[1.05] whitespace-pre-line">
              {content.chapter.heading}
            </h2>
            <p className="mt-6 md:mt-8 text-foreground-700 leading-relaxed max-w-lg text-sm md:text-base">
              {content.chapter.body}
            </p>
            <Link
              to="/artisans"
              className="mt-8 md:mt-10 inline-flex items-center gap-2 text-sm text-foreground-950 underline underline-offset-4 hover:text-primary-700 cursor-pointer whitespace-nowrap group"
            >
              Meet the maker
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
            </Link>
          </Reveal>

          <Reveal from="right" delay={120} className="lg:col-span-6 order-1 lg:order-2">
            <div className="w-full aspect-[4/5] overflow-hidden rounded-lg bg-background-100 group">
              <img
                src={content.chapter.image}
                alt={content.chapter.imageAlt}
                title={content.chapter.imageAlt}
                className="w-full h-full object-cover object-top transition-transform duration-[1800ms] ease-out group-hover:scale-[1.06]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* === Next collection === */}
      <NextCollectionLink slug={slug!} />

      <Footer />
    </main>
  );
}

function NextCollectionLink({ slug }: { slug: string }) {
  const next = getNextCollection(slug);
  if (!next) return null;

  return (
    <section className="px-4 md:px-10 lg:px-14 pb-20 md:pb-28">
      <Reveal from="up">
        <Link
          to={`/collections/${next.slug}`}
          className="group relative block w-full overflow-hidden rounded-lg cursor-pointer"
        >
          <div className="relative w-full h-[60vh] min-h-[420px]">
            <img
              src={next.heroImage}
              alt={next.heroAlt}
              title={`Next — ${next.categoryName}`}
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[2000ms] ease-out group-hover:scale-[1.04]"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/30 to-black/55 transition-opacity duration-700 group-hover:from-black/30 group-hover:via-black/35 group-hover:to-black/65" />

            <div className="relative h-full w-full flex flex-col justify-between px-6 md:px-12 py-10 md:py-14">
              <div className="flex items-center justify-between text-background-50/85">
                <span className="text-[10px] md:text-xs tracking-[0.4em] uppercase">
                  Next collection
                </span>
                <span className="hidden md:inline-flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase">
                  {String(collectionsOrderIndex(next.slug) + 1).padStart(2, "0")}
                  <span className="block w-10 h-px bg-background-50/60" />
                  {String(collections.length).padStart(2, "0")}
                </span>
              </div>

              <div className="flex items-end justify-between gap-6">
                <div className="max-w-2xl">
                  <p className="text-background-50/80 text-[10px] md:text-xs tracking-[0.3em] uppercase mb-3">
                    {next.eyebrow}
                  </p>
                  <h2 className="font-heading text-background-50 text-3xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight whitespace-pre-line">
                    {next.headline}
                  </h2>
                </div>

                <span className="shrink-0 inline-flex items-center gap-3 text-background-50 text-sm whitespace-nowrap">
                  <span className="hidden md:inline-block overflow-hidden">
                    <span className="block transition-transform duration-500 ease-out group-hover:translate-x-1">
                      Continue the story
                    </span>
                  </span>
                  <span className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-background-50/60 group-hover:bg-background-50 group-hover:text-foreground-950 transition-colors duration-500">
                    <i className="ri-arrow-right-line text-lg transition-transform duration-500 group-hover:translate-x-0.5"></i>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </Link>
      </Reveal>
    </section>
  );
}

// Tiny helper for the "01 — 05" pagination label in the next-collection card.
function collectionsOrderIndex(slug: string): number {
  return Math.max(0, collections.findIndex((c) => c.slug === slug));
}